import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, MessageCircle, Loader2, MapPin, Calendar, Users, Headset, ChevronRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
import { useTalentProfileModal } from '../../components/TalentProfileModalProvider';

/**
 * Iter-223b — UNIFIED client Messages page.
 *
 * Previously the page had two tabs ("Event Channels" / "Direct Threads")
 * which let conversations hide from the user — they'd open Messages,
 * see the wrong tab's empty state ("No conversations yet"), and assume
 * messaging was broken even though conversations existed under the
 * other tab.
 *
 * Now we merge both /messages/threads and /messages/channels into a
 * single feed sorted by last activity. Each row carries a small badge
 * indicating its source ("Event" vs "Direct") so the conversation
 * surface is preserved without forcing the user to know which API
 * holds which conversation.
 */
const ClientMessages = () => {
  const navigate = useNavigate();
  const { open: openProfile } = useTalentProfileModal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supportUnread, setSupportUnread] = useState(0);

  useEffect(() => {
    api.get('/client/messages/admin/unread-count')
      .then((r) => setSupportUnread(r.data?.unread || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([
          api.get('/messages/threads').catch(() => ({ data: [] })),
          api.get('/messages/channels').catch(() => ({ data: [] })),
        ]);
        // Normalize both sources into one shape sorted by last activity.
        const threads = (t.data || []).map((row) => ({
          kind: 'direct',
          id: row.id,
          path: `/client/messages/${row.id}`,
          title: row.other_party_name || 'Direct message',
          subtitle_parts: [row.venue_name, row.shift_date].filter(Boolean),
          shift_date: row.shift_date,
          last_at: row.last_message_at || row.updated_at || row.created_at,
          last_preview: row.last_message_preview,
          unread: row.unread_count || 0,
          photo_url: row.other_party_photo_url,
          other_party_user_id: row.other_party_user_id,
        }));
        const channels = (c.data || []).map((row) => ({
          kind: 'event',
          id: row.id,
          path: `/messages/channel/${row.id}`,
          title: row.title || 'Event channel',
          subtitle_parts: [row.venue_name, row.shift_date].filter(Boolean),
          shift_date: row.shift_date,
          last_at: row.last_message_at || row.created_at,
          last_preview: row.last_message_preview,
          unread: row.unread_count || 0,
          member_count: row.member_count,
        }));
        const merged = [...threads, ...channels].sort((a, b) => {
          const av = a.last_at ? new Date(a.last_at).getTime() : 0;
          const bv = b.last_at ? new Date(b.last_at).getTime() : 0;
          return bv - av;
        });
        setItems(merged);
      } catch (_e) {
        toast.error('Failed to load messages');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalUnread = items.reduce((s, i) => s + (i.unread || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-messages-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/home')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-blue-600" />
            Messages
            {totalUnread > 0 && (
              <Badge className="ml-1 bg-rose-500 text-white" data-testid="messages-total-unread">{totalUnread}</Badge>
            )}
          </h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Pinned Evie assistant — always available. */}
        <button
          onClick={() => navigate('/client/messages/evie')}
          className="w-full mb-3 bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 hover:border-violet-400 hover:shadow-sm transition-all rounded-2xl px-4 py-3.5 text-left flex items-center gap-3"
          data-testid="client-evie-entry"
        >
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900">Ask Evie</p>
            <p className="text-xs text-gray-500 mt-0.5">Instant help with events, recaps &amp; billing — she can loop in our team too</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
        </button>

        {/* Pinned iEvntür Support conversation — always available. */}
        <button
          onClick={() => navigate('/client/messages/support')}
          className="w-full mb-4 bg-white border border-blue-200 hover:border-blue-400 hover:shadow-sm transition-all rounded-2xl px-4 py-3.5 text-left flex items-center gap-3"
          data-testid="client-support-entry"
        >
          <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <Headset className="w-5 h-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-gray-900">iEvntür Support</p>
              {supportUnread > 0 && (
                <Badge className="bg-rose-500 text-white" data-testid="support-unread-badge">{supportUnread} new</Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">Message our team about bookings, billing or staffing · Call 404-482-0018</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
        </button>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : items.length === 0 ? (
          <Card data-testid="messages-empty-state">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No conversations yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Once you book talent or your team is staffed for an event, conversations appear here.
              </p>
              <div className="flex items-center justify-center gap-2">
                <Button
                  onClick={() => navigate('/client/discover')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="empty-discover-talent-btn"
                >
                  Discover Talent
                </Button>
                <Button variant="outline" onClick={() => navigate('/client/shifts')}>
                  View Shifts
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="unified-messages-list">
            {items.map((row) => (
              <Card
                key={`${row.kind}:${row.id}`}
                className="hover:shadow-md transition cursor-pointer"
                onClick={() => navigate(row.path)}
                data-testid={`message-row-${row.kind}-${row.id}`}
              >
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {row.kind === 'direct' ? (
                      row.photo_url ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); if (row.other_party_user_id) openProfile(row.other_party_user_id, { surface: 'message_thread' }); }}
                          className="w-11 h-11 rounded-full overflow-hidden border border-gray-200 shrink-0 hover:ring-2 hover:ring-cyan-300 transition"
                          title={`View ${row.title}'s profile`}
                        >
                          <img src={row.photo_url} alt="" className="w-full h-full object-cover" />
                        </button>
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <span className="text-sm font-semibold text-blue-700">
                            {(row.title || '?').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      )
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{row.title}</p>
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 ${row.kind === 'event' ? 'bg-cyan-100 text-cyan-700' : 'bg-blue-100 text-blue-700'}`}
                          data-testid={`message-kind-badge-${row.kind}-${row.id}`}
                        >
                          {row.kind === 'event' ? 'Event' : 'Direct'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5 flex-wrap">
                        {row.subtitle_parts.includes(row.subtitle_parts[0]) && row.subtitle_parts[0] && (
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {row.subtitle_parts[0]}</span>
                        )}
                        {row.shift_date && (
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDateMonthDay(row.shift_date)}</span>
                        )}
                        {row.member_count && (
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {row.member_count}</span>
                        )}
                      </div>
                      {row.last_preview && (
                        <p className="text-xs text-gray-400 truncate mt-1">{row.last_preview}</p>
                      )}
                    </div>
                  </div>
                  {row.unread > 0 && (
                    <Badge className="bg-rose-500 text-white shrink-0" data-testid={`message-unread-${row.kind}-${row.id}`}>
                      {row.unread} new
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientMessages;
